import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';

import { getCoverageDisplayLabel, getCoverageOrderLabel } from '../store/hooks';
import type { PatientCoverage } from '../types';

import { CoverageStatusChip, formatCoverageWindow } from './CoverageStatusChip';

interface InsuranceHistorySectionProps {
  history?: PatientCoverage[];
  onOpen?: () => void;
}

/**
 * Collapsed insurance-history list (GDN-2411): expired/cancelled plans stay
 * visible for retro-billing and COB context instead of vanishing. Read-only —
 * a history row is still used automatically for claims whose date of service
 * falls inside its window.
 */
export const InsuranceHistorySection: React.FC<InsuranceHistorySectionProps> = ({
  history = [],
  onOpen,
}) => {
  if (!history.length) {
    return null;
  }

  return (
    <Accordion
      disableGutters
      elevation={0}
      onChange={(_event, expanded) => {
        if (expanded) onOpen?.();
      }}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" color="text.secondary">
          Insurance history ({history.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1.5}>
          {history.map(coverage => {
            const window = formatCoverageWindow(coverage);
            return (
              <Box
                key={coverage.patient_insurance_coverage_id}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.25,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.default',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {getCoverageDisplayLabel(coverage) ?? 'Unknown payer'}
                    </Typography>
                    <CoverageStatusChip coverage={coverage} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {[
                      getCoverageOrderLabel(coverage.coverage_order),
                      coverage.member_id ? `Member ${coverage.member_id}` : null,
                      window,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
              </Box>
            );
          })}
          <Typography variant="caption" color="text.secondary">
            Past plans are still used automatically for claims with a date of
            service inside their coverage window.
          </Typography>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
